import React, { useRef, useState, useLayoutEffect, useMemo } from "react";
import { Component, ChartConfig } from "@/types";
import { useQueryTemplateData, isQueryTemplateDataSource, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { resolveColorArray, resolveCSSVariable } from "@/utils/cssVariableResolver";
import { fullyDecodeHtmlEntities } from "@/utils/htmlEntities";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";
import { AlertCircle } from "lucide-react";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface VerticalGaugeChartProps {
  component: Component;
  isPreview?: boolean;
}

const defaultData = { value: 65, min: 0, max: 100 };
const defaultColors = ["#3b82f6", "#2563eb"];
const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!/^-?\d+(?:\.\d+)?(?:px)?$/i.test(normalized)) {
      return undefined;
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

export const VerticalGaugeChart: React.FC<VerticalGaugeChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Get dataSource configuration
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);
  const templateId = properties.queryTemplateId as string | undefined;

  // Determine the value field
  // Priority: explicit valueKey > backend lineKeys[0] > query definition > default
  const valueField = (properties.valueKey as string)
    || (properties.valueField as string)
    || ((properties as Record<string, unknown>).lineKeys as string[] | undefined)?.[0]
    || (dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition)[0] : null)
    || "value";

  // Fetch data directly (matching SharedDataTextComponent pattern)
  const {
    data: queryData,
    isLoading: loading,
    error,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
  });

  // Extract value from query data (matching SharedDataTextComponent pattern)
  const extractedValue = useMemo<number | null>(() => {
    if (hasQueryTemplate && queryData && queryData.length > 0) {
      const row = queryData[0];
      let val = row[valueField];
      // Fallback: try 'value' field (standard pipeline projection)
      if ((val === undefined || val === null) && valueField !== 'value') {
        val = row['value'];
      }
      // Fallback: find first numeric non-metadata field
      // Handles field name mismatches (e.g. query alias 'percentage' vs actual 'percent_full')
      if (val === undefined || val === null) {
        const skipKeys = new Set(['_id', 'timestamp', 'date', 'time', 'createdAt', 'updatedAt']);
        for (const key of Object.keys(row)) {
          if (!skipKeys.has(key)) {
            const candidate = row[key];
            if (typeof candidate === 'number' || (typeof candidate === 'string' && !isNaN(Number(candidate)))) {
              val = candidate;
              break;
            }
          }
        }
      }
      if (val !== undefined && val !== null) {
        const num = typeof val === 'number' ? val : parseFloat(String(val));
        return isNaN(num) ? null : num;
      }
    }
    return null;
  }, [hasQueryTemplate, queryData, valueField]);

  // Resolve final numeric value: extracted data > static property > default
  const value = extractedValue ?? (typeof properties.value === 'number' ? properties.value : defaultData.value);

  const minValue = (properties.min as number) ?? defaultData.min;
  const maxValue = (properties.max as number) ?? defaultData.max;

  // Clamp value within range
  const clampedValue = Math.min(Math.max(value, minValue), maxValue);
  const fillPercentage = ((clampedValue - minValue) / (maxValue - minValue)) * 100;


  // Get size configuration
  const sizeConfig = getChartSizeConfig(
    toOptionalNumber(size?.width),
    toOptionalNumber(size?.height),
  );

  // Get responsive configuration
  const title = properties.title === undefined ? "Vertical Gauge" : (properties.title as string);
  const description = properties.description as string;
  const unit = fullyDecodeHtmlEntities((properties.unit as string) || "%");
  const label = properties.label as string | undefined;
  const showScale = properties.showScale !== false;
  const showValue = properties.showValue !== false;
  const valuePosition = (properties.valuePosition as string) || "center";
  const scalePosition = (properties.scalePosition as string) || "right";
  const borderColor = normalizeLegacySemanticStyleColor(
    "borderColor",
    readString(properties.borderColor) || "hsl(var(--border))",
    component,
  );
  const borderWidth = typeof properties.borderWidth === 'number' ? properties.borderWidth : 2;
  const borderRadius = typeof properties.borderRadius === 'number' ? properties.borderRadius : 12;
  const backgroundColor = normalizeLegacySemanticStyleColor(
    "backgroundColor",
    readString(properties.backgroundColor) || "transparent",
    component,
  );
  const animation = properties.animation !== false;
  const fillOpacity = typeof properties.fillOpacity === 'number' ? properties.fillOpacity : 1;
  const fillPadding = typeof properties.fillPadding === 'number' ? properties.fillPadding : 0;
  const valueColor = normalizeLegacySemanticStyleColor(
    "fill",
    readString(properties.valueColor) || "hsl(var(--foreground))",
    component,
  );
  const scaleColor = normalizeLegacySemanticStyleColor(
    "fill",
    readString(properties.scaleColor) || "hsl(var(--muted-foreground))",
    component,
  );

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    undefined,
    false,
    false
  );

  // Resolve CSS variables in colors
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Resolve colors after DOM is mounted
  useLayoutEffect(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [properties.colors, component.id, paletteChangeCounter]);

  // Resolve border and background colors
  const resolvedBorderColor = useMemo(() => {
    return resolveCSSVariable(borderColor, containerRef.current || undefined);
  }, [borderColor, paletteChangeCounter]);

  const resolvedBackgroundColor = useMemo(() => {
    return resolveCSSVariable(backgroundColor, containerRef.current || undefined);
  }, [backgroundColor, paletteChangeCounter]);

  // SVG dimensions and calculations - taller aspect ratio for vertical gauge
  const svgWidth = 140;
  const svgHeight = 300;
  const padding = 10;
  const scaleWidth = showScale && !sizeConfig.isVerySmall ? 30 : 0;
  // Shift gauge right when scale is on the left to make room for scale markers
  const scaleOffset = showScale && scalePosition === "left" ? scaleWidth : 0;
  const gaugeWidth = svgWidth - 2 * padding - scaleWidth;
  const gaugeHeight = svgHeight - 2 * padding;
  const gaugeX = padding + scaleOffset;
  const gaugeY = padding;

  // Liquid fill calculations (inset by fillPadding for "contained fill" look)
  const fillX = gaugeX + fillPadding;
  const fillWidth = Math.max(0, gaugeWidth - 2 * fillPadding);
  const fillAreaHeight = gaugeHeight - 2 * fillPadding;
  const fillAreaY = gaugeY + fillPadding;
  const fillHeight = (fillAreaHeight * fillPercentage) / 100;
  const fillY = fillAreaY + fillAreaHeight - fillHeight;
  const fillRadius = Math.max(0, borderRadius - fillPadding);

  // Calculate scale markers
  const calculateMarkers = (): Array<{ value: number; y: number }> => {
    const interval = typeof properties.scaleInterval === 'number'
      ? properties.scaleInterval
      : calculateAutoInterval(minValue, maxValue);
    const markers: Array<{ value: number; y: number }> = [];

    for (let val = minValue; val <= maxValue; val += interval) {
      const percent = ((val - minValue) / (maxValue - minValue)) * 100;
      const y = gaugeY + gaugeHeight - (gaugeHeight * percent) / 100;
      markers.push({ value: val, y });
    }

    return markers;
  };

  const calculateAutoInterval = (min: number, max: number): number => {
    const range = max - min;
    if (range <= 10) return 1;
    if (range <= 50) return 5;
    if (range <= 100) return 10;
    if (range <= 200) return 20;
    return 25;
  };

  const markers = useMemo(() => calculateMarkers(), [minValue, maxValue, properties.scaleInterval]);

  // Scale position - scale markers X position
  // When scalePosition is "left", markers are drawn at the left side (near padding)
  // When scalePosition is "right", markers are drawn to the right of the gauge
  const scaleX = scalePosition === "left" ? padding : gaugeX + gaugeWidth + 5;

  // Container style - size to content, don't expand infinitely
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: (typeof properties.minHeight === 'string' || typeof properties.minHeight === 'number')
      ? properties.minHeight
      : undefined,
    marginBottom: typeof properties.marginBottom === 'number' ? `${properties.marginBottom}px` : undefined,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div className="w-full" style={containerStyle} ref={containerRef}>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {loading ? (
          <ChartLoadingSkeleton variant="vertical-gauge" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : (
          <>
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                height: '100%',
                width: '100%',
                maxHeight: sizeConfig.isVerySmall ? '180px' : sizeConfig.isSmall ? '280px' : '360px',
              }}
            >
              <defs>
                <linearGradient
                  id={`gradient-${component.id}`}
                  x1="0%"
                  y1="100%"
                  x2="0%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor={resolvedColors[0]} />
                  <stop offset="100%" stopColor={resolvedColors[1] || resolvedColors[0]} />
                </linearGradient>
                {fillPadding > 0 && (
                  <clipPath id={`tank-clip-${component.id}`}>
                    <rect
                      x={fillX}
                      y={fillAreaY}
                      width={fillWidth}
                      height={fillAreaHeight}
                      rx={fillRadius}
                      ry={fillRadius}
                    />
                  </clipPath>
                )}
                <linearGradient
                  id={`highlight-${component.id}`}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="white" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Tank border */}
              <rect
                x={gaugeX}
                y={gaugeY}
                width={gaugeWidth}
                height={gaugeHeight}
                fill={resolvedBackgroundColor}
                stroke={resolvedBorderColor}
                strokeWidth={borderWidth}
                rx={borderRadius}
                ry={borderRadius}
              />

              {/* Liquid fill */}
              <rect
                x={fillX}
                y={fillY}
                width={fillWidth}
                height={fillHeight}
                fill={`url(#gradient-${component.id})`}
                rx={fillRadius}
                ry={fillRadius}
                opacity={fillOpacity}
                clipPath={fillPadding > 0 ? `url(#tank-clip-${component.id})` : undefined}
                style={{
                  transition: animation ? 'y 0.3s ease, height 0.3s ease' : 'none'
                }}
              />

              {/* Liquid surface highlight (meniscus shine) */}
              {fillHeight > 0 && (
                <rect
                  x={fillX}
                  y={fillY}
                  width={fillWidth}
                  height={Math.min(fillHeight, fillAreaHeight * 0.18)}
                  fill={`url(#highlight-${component.id})`}
                  rx={fillRadius}
                  ry={fillRadius}
                  clipPath={fillPadding > 0 ? `url(#tank-clip-${component.id})` : undefined}
                  style={{
                    transition: animation ? 'y 0.3s ease, height 0.3s ease' : 'none',
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Scale markers */}
              {showScale && !sizeConfig.isVerySmall && markers.map((marker) => (
                <g key={`${marker.value}-${marker.y}`}>
                  <line
                    x1={scalePosition === "left" ? scaleX + scaleWidth - 5 : gaugeX + gaugeWidth}
                    y1={marker.y}
                    x2={scalePosition === "left" ? scaleX + scaleWidth : gaugeX + gaugeWidth + 5}
                    y2={marker.y}
                    stroke={scaleColor}
                    strokeWidth={1}
                  />
                  <text
                    x={scalePosition === "left" ? scaleX + scaleWidth - 8 : gaugeX + gaugeWidth + 8}
                    y={marker.y + 3}
                    fontSize="10"
                    fill={scaleColor}
                    textAnchor={scalePosition === "left" ? "end" : "start"}
                  >
                    {marker.value}
                  </text>
                </g>
              ))}

              {/* Value label */}
              {showValue && (
                <text
                  x={gaugeX + gaugeWidth / 2}
                  y={valuePosition === "top"
                    ? gaugeY + 30
                    : valuePosition === "bottom"
                      ? gaugeY + gaugeHeight - 15
                      : gaugeY + gaugeHeight / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={sizeConfig.isVerySmall ? 20 : sizeConfig.isSmall ? 24 : 32}
                  fontWeight="700"
                  fontFamily="Inter, system-ui, -apple-system, sans-serif"
                  fill={valueColor}
                  letterSpacing="-0.02em"
                  style={{ pointerEvents: 'none' }}
                >
                  {clampedValue}{unit}
                </text>
              )}
            </svg>
          </>
        )}
      </div>
    </div>
  );
};
